import { useCallback, useEffect, useRef, useState } from "react";
import {
  NO_STATUS_FILTER,
  StatusCodeFilter,
  statusFilterKey,
} from "../statusCodeFilter";
import { VehicleMapDataPoint } from "../../../api/types/VehicleMap";
import { SPEED_UNITS } from "../../admin-details/account/constants";
import { tripDisplayNumberOr, tripDisplayNumbers } from "./tripNumbering";
import { VehicleGroup } from "../../map-multi-vehicle/useMultiVehicleTripData";
import { TMapRef } from "../type";
import { RouteRenderMode, TimedPathPoint } from "./resolveRoutePath";
import { resolveRouteLineStyle } from "./routeLineStyle";
import { overlayRouteModes } from "./routeViewModes";
import { createRouteOverlayLines } from "./routeOverlayLines";
import { createTooltipController } from "./tooltip";
import { DEFAULT_REPLAY_SPEED } from "./ReplayControlsPanel";
import {
  renderMultiVehicleRoutes,
  SelectedVehicleTrip,
  TripPointClickSource,
  tripKeyFor,
} from "./renderMultiVehicleRoutes";

type RenderHandle = ReturnType<typeof renderMultiVehicleRoutes>;

// stable identity - a `= []` default would be a fresh array on every render
// and would restart the pin-building effect on every progress tick
const NO_DETAIL_KEYS: string[] = [];

export const useMultiVehicleMapRender = ({
  mapRef,
  vehicleGroups,
  selectedTripKeys,
  resolvedPathsRef,
  isDataReady,
  routeViewsVersion,
  hoveredTripKey = null,
  detailedTripKeys = NO_DETAIL_KEYS,
  activeDetailTripKey = null,
  filteredStatusCodes = NO_STATUS_FILTER,
  overlapsEnabled = false,
  paused = false,
  speedUnit,
  timezone,
  onMapHoverTrip,
  onTripPointClick,
  onClusterTripSelect,
}: {
  mapRef: React.MutableRefObject<TMapRef>;
  vehicleGroups: VehicleGroup[];
  selectedTripKeys: Set<string>;
  // whatever has been road-aligned so far. read at draw time only - trips
  // still resolving fall back to their raw fixes and are upgraded in place.
  resolvedPathsRef: React.MutableRefObject<Map<string, TimedPathPoint[]>>;
  isDataReady: boolean;
  routeViewsVersion: number;
  hoveredTripKey?: string | null;
  // the trips whose detail is open - one trip, or a whole asset's day.
  // their event pins show and everything else fades to context.
  detailedTripKeys?: string[];
  // one trip singled out from that set - the rest drop back and only this
  // one keeps its event pins
  activeDetailTripKey?: string | null;
  // event types to pin for the detailed trip; empty means pin everything
  // null = Event Filter not applied (pin everything); [] = pin nothing
  filteredStatusCodes?: StatusCodeFilter;
  // off by default - detection cost isn't worth paying until someone asks
  overlapsEnabled?: boolean;
  // true while an event replay has the map to itself. the routes come down
  // rather than being drawn over - same as the single-asset multi-day view.
  paused?: boolean;
  // the account's configured speed unit, for the hover tooltip
  speedUnit?: SPEED_UNITS;
  // the account's timezone, for the stacked-event list's times. Read when
  // the renderer is built, so a change mid-session lands on the next redraw.
  timezone?: string;
  onMapHoverTrip?: (tripKey: string | null) => void;
  onTripPointClick?: (
    tripKey: string,
    point: VehicleMapDataPoint,
    source?: TripPointClickSource
  ) => void;
  onClusterTripSelect?: (tripKey: string) => void;
}) => {
  const cleanupRef = useRef<() => void>(() => {});
  const renderHandleRef = useRef<RenderHandle | null>(null);
  // outlives a redraw: the resolved comparison geometry is expensive and
  // the layer caches it, so a redraw reuses what it already has
  const overlayLinesRef = useRef<ReturnType<
    typeof createRouteOverlayLines
  > | null>(null);
  const hasRenderedRef = useRef(false);
  const selectedTripsSignatureRef = useRef<string | null>(null);
  const [isAnimating, setIsAnimating] = useState(false);
  // set only by an explicit Replay All, unlike isAnimating which the
  // initial reveal also sets
  const [isReplayingAll, setIsReplayingAll] = useState(false);
  const [progress, setProgress] = useState<number | null>(null);
  // the universal clock: the real timestamp the reveal/replay is currently
  // laying down. covers both the initial reveal and Replay All - they share
  // playTimelineAnimation and its one wall clock.
  const [replayClock, setReplayClock] = useState<number | null>(null);
  const [replaySpeed, setReplaySpeed] = useState<number>(DEFAULT_REPLAY_SPEED);
  const replaySpeedRef = useRef(replaySpeed);
  replaySpeedRef.current = replaySpeed;
  // the ref as well as the state: an animation starting in this same tick
  // reads the ref before React has re-rendered
  const resetSpeed = () => {
    replaySpeedRef.current = DEFAULT_REPLAY_SPEED;
    setReplaySpeed(DEFAULT_REPLAY_SPEED);
  };
  // detail-view route draw, tracked separately from the replay flags so
  // the two don't fight over the controls panel
  const [isDetailAnimating, setIsDetailAnimating] = useState(false);
  const [detailProgress, setDetailProgress] = useState<number | null>(null);
  const [detailClock, setDetailClock] = useState<number | null>(null);
  // the asset's reported speed at the moment on the detail clock, and only
  // while a single trip is drawing - a set of them is several assets at
  // once, so the renderer leaves it undefined there
  const [detailSpeedKph, setDetailSpeedKph] = useState<number | null>(null);
  // the open trip's route has been drawn all the way through (or skipped to
  // the end). Without this the panel reads as "finished" for the frame
  // between a trip opening and its draw effect running, flashing a Replay
  // button over a route that has not drawn yet.
  const [detailDrawComplete, setDetailDrawComplete] = useState(false);
  // one flag covers both the timeline replay and the detail draw - the
  // renderer's pause gate is shared between them since only one ever runs
  const [isPaused, setIsPaused] = useState(false);

  // read in the draw effect without being deps of it, so opening a trip's
  // detail doesn't retrigger a full redraw
  const detailedTripKeysRef = useRef(detailedTripKeys);
  detailedTripKeysRef.current = detailedTripKeys;
  // identity-independent, so a re-render with an equivalent array doesn't
  // restart the detail draw
  const detailSignature = detailedTripKeys.join("|");
  const filteredStatusCodesRef = useRef(filteredStatusCodes);
  filteredStatusCodesRef.current = filteredStatusCodes;
  const activeDetailTripKeyRef = useRef(activeDetailTripKey);
  activeDetailTripKeyRef.current = activeDetailTripKey;
  const overlapsEnabledRef = useRef(overlapsEnabled);
  overlapsEnabledRef.current = overlapsEnabled;

  useEffect(() => {
    const map = mapRef.current?.map;
    if (!map || !isDataReady || paused) {
      cleanupRef.current();
      cleanupRef.current = () => {};
      return;
    }

    // A route-mode change can happen because the user zoomed the map. Fitting
    // bounds during that redraw immediately undoes the user's zoom.
    const selectedTripsSignature = Array.from(selectedTripKeys)
      .sort()
      .join("|");
    const shouldFitBounds =
      !hasRenderedRef.current ||
      selectedTripsSignatureRef.current !== selectedTripsSignature;
    selectedTripsSignatureRef.current = selectedTripsSignature;

    cleanupRef.current();
    cleanupRef.current = () => {};

    const trips: SelectedVehicleTrip[] = [];
    vehicleGroups.forEach((group) => {
      // the same numbering the trip list shows, so a route's tooltip and
      // its row agree - see tripNumbering
      const displayNumbers = tripDisplayNumbers(group.trips);
      group.trips.forEach((points, tripIndex) => {
        const tripKey = tripKeyFor(group.deviceId, tripIndex);
        if (!selectedTripKeys.has(tripKey)) return;
        // drawn now on the fixes we already have; the road-aligned path
        // replaces it in place as soon as it arrives
        const path =
          resolvedPathsRef.current.get(tripKey) ??
          points.map((point) => ({
            lat: point.latitude,
            lng: point.longitude,
            timestamp: point.timestamp,
          }));
        if (path.length < 2) return;
        trips.push({
          deviceId: group.deviceId,
          vehicleIndex: group.vehicleIndex,
          vehicleLabel: group.label,
          vehicleFullLabel: group.fullLabel,
          tripIndex,
          tripNumber: tripDisplayNumberOr(displayNumbers, tripIndex),
          color: group.color,
          points,
          path,
        });
      });
    });

    // the stacked comparison views, drawn under the real line
    if (!overlayLinesRef.current)
      overlayLinesRef.current = createRouteOverlayLines({ map });
    overlayLinesRef.current.setTrips(
      trips.map((trip) => ({
        tripKey: tripKeyFor(trip.deviceId, trip.tripIndex),
        color: trip.color,
      }))
    );
    overlayLinesRef.current.setModes(
      overlayRouteModes(mapRef.current?.routeViewsEnabled)
    );

    const tooltip = createTooltipController();
    const renderHandle = renderMultiVehicleRoutes({
      map,
      trips,
      lineStyle: resolveRouteLineStyle(mapRef.current?.routeViewsEnabled),
      tooltip,
      speedUnit,
      timezone,
      onHoverTrip: onMapHoverTrip,
      onTripPointClick,
      onClusterTripSelect,
    });
    renderHandleRef.current = renderHandle;
    hasRenderedRef.current = true;
    if (shouldFitBounds) renderHandle.fitBoundsIfAny();
    // this redraw wiped everything, so restore any open detail pins
    renderHandle.setDetailedTrips(
      detailedTripKeysRef.current,
      filteredStatusCodesRef.current
    );
    renderHandle.setActiveDetailTrip(activeDetailTripKeyRef.current);
    renderHandle.setOverlapsEnabled(overlapsEnabledRef.current);

    // wait for tiles and the fitBounds move to settle before animating
    let tornDown = false;
    const google = (window as any).google;
    const startAnimation = () => {
      if (tornDown) return;
      // A rebuild with a trip's detail already open - an event replay
      // finishing, a refresh landing - is a return to that screen, not an
      // arrival at the overview. Replaying every route and then re-opening
      // the detail is not what "go back" means, so the finished picture
      // just goes straight up.
      if (detailedTripKeysRef.current.length > 0) {
        renderHandle.drawWithoutAnimating();
        return;
      }
      setIsAnimating(true);
      setProgress(0);
      setReplayClock(null);
      renderHandle
        .playTimelineAnimation(
          () => replaySpeedRef.current,
          (percent, clock) => {
            setProgress(percent);
            setReplayClock(clock ?? null);
          }
        )
        .then(() => {
          setIsAnimating(false);
          setProgress(null);
          setReplayClock(null);
        });
    };
    const idleListener = shouldFitBounds
      ? google?.maps?.event?.addListenerOnce(map, "idle", startAnimation)
      : null;
    if (!shouldFitBounds) startAnimation();

    cleanupRef.current = () => {
      tornDown = true;
      if (idleListener) google.maps.event.removeListener(idleListener);
      renderHandle.clear();
      // the comparison lines go with the routes they annotate; the layer
      // itself and its resolved geometry survive for the next draw
      overlayLinesRef.current?.clear();
      tooltip.destroy();
      renderHandleRef.current = null;
    };

    return () => cleanupRef.current();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vehicleGroups, selectedTripKeys, isDataReady, paused]);

  // the layer outlives redraws, so it is torn down with the page
  useEffect(
    () => () => {
      overlayLinesRef.current?.destroy();
      overlayLinesRef.current = null;
    },
    []
  );

  useEffect(() => {
    renderHandleRef.current?.setLineStyle(
      resolveRouteLineStyle(mapRef.current?.routeViewsEnabled)
    );
    overlayLinesRef.current?.setModes(
      overlayRouteModes(mapRef.current?.routeViewsEnabled)
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [routeViewsVersion]);

  // one stacked view's geometry for one trip, as useRouteOverlayPaths
  // resolves it
  const applyOverlayPath = useCallback(
    (tripKey: string, mode: RouteRenderMode, path: TimedPathPoint[]) => {
      overlayLinesRef.current?.setPath(tripKey, mode, path);
    },
    []
  );

  // stable identities, so the resolver isn't restarted by a re-render
  const applyResolvedPath = useCallback(
    (tripKey: string, path: TimedPathPoint[], immediate?: boolean) => {
      renderHandleRef.current?.updateTripPath(tripKey, path, immediate);
    },
    []
  );

  const refreshOverlapClusters = useCallback(() => {
    renderHandleRef.current?.refreshOverlapClusters();
  }, []);

  useEffect(() => {
    renderHandleRef.current?.setHighlightedTrip(hoveredTripKey);
  }, [hoveredTripKey]);

  // focused trip's event pins and zoom. whether the pins are held back for
  // the animation to reveal is decided in setDetailedTrip.
  useEffect(() => {
    renderHandleRef.current?.setDetailedTrips(
      detailedTripKeys,
      filteredStatusCodes
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [detailSignature, statusFilterKey(filteredStatusCodes)]);

  useEffect(() => {
    renderHandleRef.current?.setActiveDetailTrip(activeDetailTripKey);
    const openTripKeys =
      detailedTripKeys.length > 0
        ? detailedTripKeys
        : Array.from(selectedTripKeys);
    if (openTripKeys.length === 0) return;
    const tripsToFit =
      activeDetailTripKey && detailedTripKeys.includes(activeDetailTripKey)
        ? [activeDetailTripKey]
        : openTripKeys;
    void renderHandleRef.current?.fitBoundsToTrips(tripsToFit);
    // detailSignature keeps the map aligned with the current left-panel set.
    // Route-type changes only update line styling and must not refit the map.
  }, [activeDetailTripKey, detailSignature, selectedTripKeys]);

  // toggling this is a styling/visibility change, not a redraw - the
  // vehicle legend panel flips it live
  useEffect(() => {
    renderHandleRef.current?.setOverlapsEnabled(overlapsEnabled);
  }, [overlapsEnabled]);

  // opening a trip redraws its route slowly, with its events dropping in
  // just behind the head. keyed on the open set only, so changing the
  // event filter in there doesn't restart it.
  useEffect(() => {
    if (detailedTripKeys.length === 0) {
      // backing out mid-draw would otherwise leave those routes still
      // unfolding behind the list
      renderHandleRef.current?.stopDetailAnimation();
      setIsDetailAnimating(false);
      setDetailProgress(null);
      setDetailClock(null);
      setDetailSpeedKph(null);
      setDetailDrawComplete(false);
      setIsPaused(false);
      resetSpeed();
      return;
    }
    resetSpeed();
    setIsDetailAnimating(true);
    setDetailProgress(0);
    setDetailClock(null);
    setDetailSpeedKph(null);
    setDetailDrawComplete(false);
    setIsPaused(false);
    const renderHandle = renderHandleRef.current;
    const tripsToFit =
      activeDetailTripKey && detailedTripKeys.includes(activeDetailTripKey)
        ? [activeDetailTripKey]
        : detailedTripKeys;
    (async () => {
      if (!renderHandle) return;
      await renderHandle.fitBoundsToTrips(tripsToFit);
      await renderHandle.playDetailAnimation(
        detailedTripKeys,
        () => replaySpeedRef.current,
        (percent, clock, speedKph) => {
          setDetailProgress(percent);
          setDetailClock(clock ?? null);
          setDetailSpeedKph(speedKph ?? null);
        }
      );
      setIsDetailAnimating(false);
      setDetailProgress(null);
      setDetailClock(null);
      setDetailSpeedKph(null);
      setDetailDrawComplete(true);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [detailSignature]);

  const stopDetailAnimation = () => {
    renderHandleRef.current?.stopDetailAnimation();
    setIsDetailAnimating(false);
    setDetailProgress(null);
    setDetailClock(null);
    setDetailSpeedKph(null);
    // skipping the draw leaves the finished route up, which is just as
    // replayable as one that ran to the end on its own
    setDetailDrawComplete(true);
    setIsPaused(false);
    resetSpeed();
  };

  // Re-runs the detail draw for whatever's currently open, on demand -
  // for a caller (the compare view's expanded map) that's permanently in
  // detail mode and wants its own "Replay" button. Deliberately not
  // playTimelineAnimation: that one wipes every detail pin at the start
  // for the main map's overview replay, which would otherwise be perfect
  // but has nothing to bring the pins back afterwards here, where detail
  // mode never turns off.
  const replayDetail = () => {
    const handle = renderHandleRef.current;
    if (!handle || detailedTripKeys.length === 0 || isDetailAnimating) return;
    setIsDetailAnimating(true);
    setDetailProgress(0);
    setDetailClock(null);
    setDetailSpeedKph(null);
    setDetailDrawComplete(false);
    setIsPaused(false);
    // the pins are all still sitting there from the last time this
    // finished (or from the initial draw) - hide them and forget that
    // draw happened, or the animation below finds nothing left to reveal
    handle.resetDetailPinsForReplay();
    handle
      .playDetailAnimation(
        detailedTripKeys,
        () => replaySpeedRef.current,
        (percent, clock, speedKph) => {
          setDetailProgress(percent);
          setDetailClock(clock ?? null);
          setDetailSpeedKph(speedKph ?? null);
        }
      )
      .then(() => {
        setIsDetailAnimating(false);
        setDetailProgress(null);
        setDetailClock(null);
        setDetailSpeedKph(null);
        setDetailDrawComplete(true);
      });
  };

  // Replay All: every selected trip against one wall clock, so vehicles
  // that were out at the same time unfold together and ones that weren't
  // fall naturally into order. Slower than the reveal on load.
  const replay = () => {
    const handle = renderHandleRef.current;
    if (!handle || isAnimating) return;
    resetSpeed();
    setIsAnimating(true);
    setIsReplayingAll(true);
    setProgress(0);
    setReplayClock(null);
    setIsPaused(false);
    handle
      .playTimelineAnimation(
        () => replaySpeedRef.current,
        (percent, clock) => {
          setProgress(percent);
          setReplayClock(clock ?? null);
        },
        handle.REPLAY_PACE
      )
      .then(() => {
        setIsAnimating(false);
        setIsReplayingAll(false);
        setProgress(null);
        setReplayClock(null);
      });
  };

  const stopReplay = () => {
    renderHandleRef.current?.stopTimelineAnimation();
    setIsAnimating(false);
    setIsReplayingAll(false);
    setProgress(null);
    setReplayClock(null);
    setIsPaused(false);
    resetSpeed();
  };

  // pauses whichever of the timeline replay or the detail draw is
  // currently running - the renderer's gate covers both
  const togglePause = () => {
    const handle = renderHandleRef.current;
    if (!handle) return;
    if (isPaused) {
      handle.resumeReplay();
      setIsPaused(false);
    } else {
      handle.pauseReplay();
      setIsPaused(true);
    }
  };

  // keeps the map's picked-event highlight in step with whatever the page
  // has selected - a list row, a map pin, or nothing
  const highlightEventPoint = useCallback(
    (point: VehicleMapDataPoint | null) => {
      renderHandleRef.current?.highlightEventPoint(point);
    },
    []
  );

  return {
    applyResolvedPath,
    applyOverlayPath,
    refreshOverlapClusters,
    highlightEventPoint,
    replay,
    stopReplay,
    isAnimating,
    isReplayingAll,
    isPaused,
    togglePause,
    progress,
    replayClock,
    replaySpeed,
    setReplaySpeed,
    isDetailAnimating,
    detailProgress,
    detailClock,
    detailSpeedKph,
    detailDrawComplete,
    stopDetailAnimation,
    replayDetail,
  };
};
